import { InjectionToken } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { ColumnItem } from '@app/models/common/column-item';
import { Constant } from '@app/models/enums/constant.enum';
import { ValueType } from '@app/models/enums/value-type.enum';
import { addMonths, differenceInCalendarMonths, format, subMonths } from 'date-fns';
import { cloneDeep, differenceWith, isArray, isBoolean, isEmpty, isEqual, isFinite, isPlainObject, snakeCase, toString, trim, values } from 'lodash';
import moment from 'moment/moment';


export const isValueEmpty = (value: any): boolean => {
  if (isBoolean(value)) return false;
  // for object, check if the object's value are all empty
  if (isPlainObject(value)) {
    // check if object contain not empty key-value
    return !values(value).some(v => !isValueEmpty(v));
  }
  // isEmpty(numberic) always true, so must check numberic first
  if (isFinite(value)) return false;
  // isEmpty can check if object, collection, map, or set is empty. and if the value is null, '', underfined or NaN
  return isEmpty(value);
};

export const isSame = (value: any, other: any): boolean => {
  if (isArray(value) && isArray(other)) {
    if (value.length !== other.length) return false;
    value = value.sort();
    other = other.sort();
    const diff = differenceWith(value, other, isEqual);
    return isEmpty(diff);
  }
  return isEqual(value, other);
};

export const isSameObject = (obj: any = {}, otherObj: any = {}): boolean => {
  if (!Object.keys(obj).length) {
    const _obj = cloneDeep(obj);
    obj = cloneDeep(otherObj);
    otherObj = cloneDeep(_obj);
  }
  const result = Object.keys(obj).every(k => {
    const val = obj[k];
    const oVal = otherObj[k];
    if (isValueEmpty(val) && isValueEmpty(oVal)) {
      return true;
    }
    if (isPlainObject(val) && isPlainObject(oVal)) {
      return isSameObject(val, oVal);
    }
    const same = isSame(val, oVal);
    return same;
  });
  return result;
};

export const isChangesPropNotEmpty = (changeProp: any): boolean => {
  return !isEmpty(changeProp) && !isValueEmpty(changeProp.currentValue);
};

export const getValidateFormValues = (form: FormGroup): any => {
  Object.values(form.controls).forEach(control => {
    control.markAsDirty();
    control.updateValueAndValidity({ onlySelf: true });
  });
  if (!form.valid) return null;
  return form.value;
};

export const updateControlValueAndValidity = (control: AbstractControl | null, isRequired: boolean): void => {
  if (control) {
    if (isRequired) {
      control.setValidators(Validators.required);
      control.markAsDirty();
    } else {
      control.clearValidators();
      control.markAsPristine();
    }
    control.updateValueAndValidity();
  }
};

export const resetForm = (form: FormGroup) => {
  form.reset();
  for (const i in form.controls) {
    if (form.controls.hasOwnProperty(i)) {
      form.controls[i].markAsDirty();
      form.controls[i].updateValueAndValidity();
    }
  }
};

export const thousandsFormatFixed = (value: any, precision: number): string => {
  const number = parseFloat(value);
  if (Number.isNaN(number)) return '';
  return (number.toFixed(precision) + '').replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,');
};

export const percentFormatFixed = (value: any, precision: number): string => {
  if (isEmpty(value) && !value) return '';
  if (value.toString().endsWith('%')) return value;
  const number = parseFloat(value);
  if (Number.isNaN(number)) return value;
  return `${(number * 100).toFixed(precision)}%`;
};

export const formatValueByType = (value: any, type?: ValueType, precision?: number): string => {
  let result = value;
  if (type) {
    switch (type) {
      case ValueType.Decimal:
        result = thousandsFormatFixed(value, precision || 0);
        break;
      case ValueType.Percent:
        result = percentFormatFixed(value, precision || 1);
        break;
      case ValueType.Float:
        result = thousandsFormatFixed(value, precision || 1);
        break;
      case ValueType.Float_2:
        result = thousandsFormatFixed(value, 2);
        break;
      case ValueType.Text:
        result = trim(value);
        break;
    }
  }
  return result;
};

export const isNumber = (value: any): boolean => {
  const reg = /^\-?[0-9]+\.?[0-9]*$/;
  return reg.test(value);
};
export const compare = (a: any, b: any): number => {
  if (isNumber(a) && isNumber(b)) {
    return a - b;
  }
  if (isValueEmpty(a)) {
    return -1;
  } else if (isValueEmpty(b)) {
    return 1;
  } else {
    return a.toString().localeCompare(b.toString(), 'zh');
  }
};

export const stringCharLength = (str: string): number => {
  return !str ? 0 : str.replace(/[^\x00-\xff]/g, '01').length;
};

export const getEnumKeys = <O, K extends keyof O = keyof O>(obj: any): K[] => {
  const enumKeys = Object.keys(obj).filter(k => Number.isNaN(+k)) as K[];
  return enumKeys;
};

export const getEnumKeyByValue = <
  TEnumKey extends string,
  TEnumVal extends string | number
>(myEnum: { [key in TEnumKey]: TEnumVal }, enumValue: TEnumVal): string  =>{
  const keys = (Object.keys(myEnum) as TEnumKey[]).filter(
    (x) => myEnum[x] === enumValue,
  );
  return keys.length > 0 ? keys[0] : '';
};
export const getSearchData = (): Record<string, string> => {
  const paramsObj: Record<string, string> = {};
  const searchParam = window.location.search;
  if (searchParam) {
    const arr = decodeURIComponent(searchParam).slice(1).split('&');
    arr.forEach(item => {
      const paramArr = item.split('=');
      paramsObj[paramArr[0]] = paramArr[1];
    })
  }
  return paramsObj;
}

export const getParameterValue = (searchParams: string, name: string) : string | null => {
  if (!searchParams) return null;
  const urlParams = new URLSearchParams(searchParams);
  const value = urlParams.get(name);
  return value !== null && value !== '' ? value : null;
}

export const toMonth = (period?: string): string => {
  if (!period) return '';
  return period.replace('14', '-');
};

export const tranType = (str: string): string | boolean | number => {
  let result: string | boolean | number = str;
  try {
    result = JSON.parse(str);
  } catch (e) {}
  return result;
};

export const getColor = (colors: Array<string>, index: number) => {
  const size = colors.length;
  const _index = (index + 1) % size;
  return colors[_index];
};

export const DEFAULT_CHART_COLORS = [
  '#00a346',
  '#f0640d',
  '#a27eff',
  '#00a1a2',
  '#ad4400',
  '#f84e83',
  '#b3ccff',
  '#00bf90',
  '#682ee0',
  '#ff6d05',
  '#6699ff',
  '#b497ff',
  '#00cdd1',
  '#E4FFE4',
  '#FDD5E1',
  '#FDE5D4',
  '#FCFABE',
  '#FCFABE',
  '#C4FEFF',
  '#D6E4FF',
  '#EBE3FF',
  '#00ca23',
  '#ad4400',
  '#138238',
  '#fab0cb',
  '#15441F',
  '#B6023B',
  '#F06400',
  '#7E5C00',
  '#008F90',
  '#360093',
  '#0056ff',
  '#ff1e68',
  '#b6023b',
  '#15441f',
  '#0136e0',
  '#10009D',
  '#d20043',
  '#1d79ff',
  '#880535',
  '#006a6b',
  '#6512f0',
];

export const NEW_CHART_COLORS = [
  '#0088d4',
  '#19ca88',
  '#858ff8',
  '#ef5f17',
  '#31d1ff',
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc',
  '#2e8cff',
  '#4fb69d',
  '#446acf',
  '#27d08a',
  '#0682ab',
  '#06b4ab',
  '#204188',
  '#e16e1c',
  '#f7e270',
  '#F37878',
  '#FFBCBC',
  '#6aa904',
  '#F38BA0',
  '#9ae919',
  '#0696ab',
  '#B2A4FF',
  '#04a947',
  '#06a0ab',
  '#9FC088',
  '#0cdf61',
  '#95CD41',
  '#D9CE3F',
  '#cf47e1',
  '#0f63d6',
  '#C0D8C0',
  '#bdb412',
  '#f06e2d',
  '#03a577',
  '#3265d2',
  '#cb4b7a',
  '#0c644a',
  '#A3A9F5',
  '#ca5113',
  '#4d9300',
  '#ea7ccc',
  '#9a60b4',
  '#fc8452',
  '#3ba272',
  '#73c0de',
  '#ee6666',
  '#fac858',
  '#91cc75',
  '#5470c6',
  '#4697e2',
  '#ffb500',
  '#23a9b2',
  '#787Ca9',
  '#31D1FF',
  '#A3A9F5',
  '#2D6DF6',
];

export const NIQ_COLORS = [
  '#0215C1',
  '#0136E0',
  '#0056FF',
  '#1D79FF',
  '#6699FF',
  '#00cdd1',
  '#ad4400',
  '#138238',
  '#fab0cb',
  '#FACFAF',
  '#F6A062',
  '#FF6D05',
  '#AD4400',
  '#7A3000',
  '#622700',
];

export const HOT_WORD_COLORS = [
  '#fd8631',
  '#e16e1c',
  '#B5EAEA',
  '#e9c619',
  '#FFBCBC',
  '#F38BA0',
  '#F37878',
  '#9ae919',
  '#B2A4FF',
  '#6aa904',
  '#04a947',
  '#9FC088',
  '#0cdf61',
  '#D9CE3F',
  '#d10865',
  '#ff3090',
  '#95CD41',
  '#F7F7F7',
  '#e730ff',
  '#cf47e1',
  '#ff4545',
  '#C0D8C0',
  '#ff3fad',
  '#c31779',
  '#bdb412',
];

export const HWTooltipKey: any = {
  overviewChart: {
    totalPromoValue: '销售额',
    promoValueShare: '销售占比',
    sppd: '单品效率',
    itemCountShare: '商品占比',
    value: '商品数',
  },
  matrixChart: {
    totalPromoValue: '销售额',
    promoGrowthRate: '销售额增速（growthType）',
  },
  sppdChart: {
    totalPromoValue: '销售额',
    value: '商品数',
    sppd: '单品效率',
    sppdGrowthRate: '单品效率增速（growthType）',
  },
  icntChart: {
    totalPromoValue: '销售额',
    value: '商品数',
    sppd: '单品效率',
    sppdGrowthRate: '单品效率增速（growthType）',
  },
};

export const OVERVIEW_COLORD = ['#81c57c', '#ebc757'];
//黄，红，绿，灰，蓝
export const MATRI_COLORD = ['#ebc757', '#f97477', '#81c57c', '#d1d1d1', '#2dbdb4'];
//灰，红，蓝，绿，黄
export const SPPD_COLORD = ['#d1d1d1', '#f97477', '#2dbdb4', '#81c57c', '#ebc757'];
//
export const ICNT_COLORD = ['#f97477', '#81c57c', '#d1d1d1', '#2dbdb4', '#ebc757'];
export const HWTooltipKeyColors: any = {
  overviewChart: OVERVIEW_COLORD,
  matrixChart: MATRI_COLORD,
  sppdChart: SPPD_COLORD,
  icntChart: ICNT_COLORD,
};

export const STACk_BAR_COLORS = ['#13e213', '#00a346', '#1a1a1a', '#2fff88', '#bdffbb', '#8cbb89cb'];

export const getMonthRange = (endMonth: string, subtract: number): string[] => {
  const startDate: Date = subMonths(new Date(endMonth), subtract);
  const startMonth = format(startDate, Constant.FORMAT_MONTH);
  return [startMonth, endMonth];
};

export const getMonthsByRange = (start: Date, end: Date): Date[] => {
  const months: Date[] = [];
  let startMonth = start;
  while (differenceInCalendarMonths(startMonth, end) <= 0) {
    months.push(startMonth);
    startMonth = addMonths(startMonth, 1);
  }
  return months;
};

export const getWeeksForCurrMonth = (): Array<{start:string, end:string}> => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month+1, 0);

  const weeks = [];
  const currentDay = new Date(firstDayOfMonth);
  let weekNumber = 0;

  let isEnd = true;
  while (isEnd) {
    const startOfWeek = new Date(currentDay);
    const endOfWeek = new Date(currentDay);
    // 获取本周的开始日期（周一）
    startOfWeek.setDate(currentDay.getDate() - currentDay.getDay() + (currentDay.getDay() === 0 ? -6 : 1));
    // 获取本周的结束日期（周日）
    endOfWeek.setDate(currentDay.getDate() - currentDay.getDay() + (currentDay.getDay() === 0 ? 0 : 7));

    weeks.push({
      start: format(startOfWeek, Constant.FORMAT_DATE),
      end: format(endOfWeek, Constant.FORMAT_DATE)});
    weekNumber++;

    if (endOfWeek < lastDayOfMonth) {
      currentDay.setDate(currentDay.getDate() + 7);
    } else {
      isEnd = false;
    }
  }

  return weeks;
}

export const getWeekendsForCurrYear = ():Array<{ start: string, end: string }> => {
  const weekends = [];
  for (let i = 0; i < 12; i++) {
    weekends.push(getWeekendsForMonth(i)[3]);
    // weekends.push(...getWeekendsForMonth(i));
  }
  return weekends;
}

export const getWeekendsForMonth = (month?: number): Array<{ start: string, end: string }> => {
  const today = new Date();
  const year = today.getFullYear();
  if (month === undefined) {
    month = today.getMonth();
  }

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  const weekends = [];
  const currentDay = new Date(firstDayOfMonth);

  while (currentDay <= lastDayOfMonth) {
    const saturday = new Date(currentDay);
    const tomorrow = new Date(currentDay);
    tomorrow.setDate(saturday.getDate() + 1);
    // 如果是周六且第二天是周日
    if (saturday.getDay() === 6 && tomorrow.getDay() === 0 && tomorrow <= lastDayOfMonth) {
      weekends.push({
        start: format(saturday, Constant.FORMAT_DATE),
        end: format(tomorrow, Constant.FORMAT_DATE)
      });
      currentDay.setDate(currentDay.getDate() + 7);
    } else {
      currentDay.setDate(currentDay.getDate() + 1);
    }
  }
  return weekends;
}

export const getMonthsFromQuarter = (year: number, quarter: number): string[] => {
  if (quarter < 1 || quarter > 4) {
    return [];
  }
  const startMonth = (quarter - 1) * 3 + 1;
  const months = [];

  for (let i = 0; i < 3; i++) {
    months.push(`${year}-${String(startMonth + i).padStart(2, '0')}`);
  }
  return months;
}


export const getMonthsFromHalfYear = (year: number, halfYear: number): string[] => {
  if (halfYear < 1 || halfYear > 2) {
    return [];
  }
  const months = [];
  const startMonth = (halfYear - 1) * 6 + 1;
  for (let i = 0; i < 6; i++) {
    months.push(`${year}-${String(startMonth + i).padStart(2, '0')}`);
  }
  return months;
}

export const getDateRange = (validDateData: string[], dateNum: number) : string[] => {
  if (isEmpty(validDateData)) return [];
  const validEnd = validDateData[validDateData.length - 1];

  if (dateNum === -1) return [validDateData[0], validEnd];

  const startDate = moment(validEnd).add(-dateNum + 1, 'd').format('YYYY-MM-DD');
  const validStart = validDateData.slice(-dateNum).find(d => d >= startDate)!
  return [validStart, validEnd];
}

export const treeToList = (
  tree: any[] = [],
  { idName = 'id', childrenName = 'children', level = 1, parent = null } = {},
): any[] => {
  const parentNodes = (tree || []).map(treeItem => ({ ...treeItem, level, parent }));
  const childrenNodes = (tree || []).map(treeItem => {
    level = Number(level) + 1;
    parent = treeItem[idName];
    return treeToList(treeItem[childrenName], { idName, childrenName, level, parent });
  });
  return Array.prototype.concat.apply(parentNodes, childrenNodes);
};

export const listToTree = (
  list: any[] = [],
  { idName = 'id', parentName = 'parent', childrenName = 'children', parent = null } = {},
): any[] => {
  const tree = (list || [])
    .filter(item => toString(item[parentName]) === toString(parent))
    .sort((item1, item2) => item1[idName] - item2[idName])
    .map(item => {
      const parent = item[idName];
      item[childrenName] = listToTree(list, { idName, parentName, childrenName, parent });
      return item;
    });
  return tree;
};

export const convertTreeToList = (root: any, keyName = 'id', isExpand = false): any[] => {
  const stack: any[] = [];
  const array: any[] = [];
  const hashMap = {};
  stack.push({ ...root, level: 0, expand: isExpand });

  while (stack.length !== 0) {
    const node = stack.pop()!;
    visitNode(node, hashMap, keyName, array);
    if (node.children) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push({ ...node.children[i], level: node.level! + 1, expand: false, parent: node });
      }
    }
  }
  return array;
};

export const visitNode = (node: any, hashMap: { [key: string]: boolean }, keyName = 'id', array: any[]): void => {
  if (!hashMap[node[keyName]]) {
    hashMap[node[keyName]] = true;
    array.push(node);
  }
};

export const collapse = (array: any[], data: any, keyName = 'id', isExpand: boolean): void => {
  if (!isExpand) {
    if (data.children) {
      data.children.forEach((d: any) => {
        const target = array.find(a => a[keyName] === d[keyName])!;
        target.expand = false;
        collapse(array, target, keyName, false);
      });
    } else {
      return;
    }
  }
};

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');
export const defaultTimeout = 600000 * 3;

/**
 * @param span series 中最大值与最小值的差值
 * @param splitNumber 坐标轴分割段数
 * @param round 折线图中需要传 true
 * @returns {number} 处理后 max 的最后结果
 */
export const nice = (span: number, splitNumber: number, round: boolean) => {
  let val = span / splitNumber;
  const exponent = Math.floor(Math.log(val) / Math.LN10);
  const exp10 = Math.pow(10, exponent);
  const f = val / exp10; // 1 <= f < 10

  let nf;

  if (round) {
    if (f < 1.5) {
      nf = 1;
    } else if (f < 2.5) {
      nf = 2;
    } else if (f < 4) {
      nf = 3;
    } else if (f < 7) {
      nf = 5;
    } else {
      nf = 10;
    }
  } else {
    if (f < 1) {
      nf = 1;
    } else if (f < 2) {
      nf = 2;
    } else if (f < 3) {
      nf = 3;
    } else if (f < 5) {
      nf = 5;
    } else {
      nf = 10;
    }
  }

  val = nf * exp10;

  const step = exponent >= -20 ? +val.toFixed(exponent < 0 ? -exponent : 0) : val;

  let result;
  for (let i = splitNumber - 3; i <= splitNumber + 3; i++) {
    result = step * i;
    if (result > span) break;
  }

  return result;
};

// KOL 默认选中的fact
export const DEFAULT_FACTS: string[] = ['fans_num', 'live_sales_value', 'sales_value_per_live', 'viewer_value'];

export const kolSortMap: any = {
  kolNickName: 'account_name',
  influenceRank: 'ir',
  marketingRank: 'mr',
  conversionRank: 'cr',
};
export const sortOrderMap = (sortField: string | null, sortOrder: string | null) => {
  const sortColumn = sortField ? (kolSortMap[sortField] ? kolSortMap[sortField] : sortField) : null;
  return {
    sortDirection: sortOrder === 'ascend' ? 'ASC' : sortOrder === 'descend' ? 'DESC' : null,
    sortColumn,
  };
};

export const formatFactColumns = (columns: ColumnItem[], factFormatMap: Map<string, string>) => {
  return columns.map(column => {
    const factColumnFmt = factFormatMap.get(snakeCase(column.key)); // 指标列
    if (factColumnFmt) {
      const formatArr = factColumnFmt.split('_');
      column.valueFormat = formatArr[0] as ValueType;
      column.digitsInfo = formatArr.length === 2 ? `1.${formatArr[1]}-${formatArr[1]}` : '';
    }
    return column;
  });
};

// 正则匹配特殊字符
export const regExp = new RegExp('[-~#（）|【-】· (){}+=*^&%$@!.,，/。<>;:：；‘’“”、\'"?`]');

export const gradientColor = (startColor: string, endColor: string, step: number) => {
  const startRGB = colorRgb(startColor); //转换为rgb数组模式
  const startR = startRGB[0];
  const startG = startRGB[1];
  const startB = startRGB[2];
  const endRGB = colorRgb(endColor);
  const endR = endRGB[0];
  const endG = endRGB[1];
  const endB = endRGB[2];
  const sR = (endR - startR) / step; //总差值
  const sG = (endG - startG) / step;
  const sB = (endB - startB) / step;
  const colorArr = [];
  for (let i = 0; i < step; i++) {
    //计算每一步的hex值
    const hex = colorHex(
      'rgb(' + parseInt(sR * i + startR) + ',' + parseInt(sG * i + startG) + ',' + parseInt(sB * i + startB) + ')',
    );
    colorArr.push(hex);
  }
  return colorArr;
};
export const colorRgb = (sColors: any) => {
  const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
  let sColor = sColors.toLowerCase();
  if (sColor && reg.test(sColor)) {
    if (sColor.length === 4) {
      let sColorNew = '#';
      for (let i = 1; i < 4; i += 1) {
        sColorNew += sColor.slice(i, i + 1).concat(sColor.slice(i, i + 1));
      }
      sColor = sColorNew;
    }
    //处理六位的颜色值
    const sColorChange = [];
    for (let i = 1; i < 7; i += 2) {
      sColorChange.push(parseInt('0x' + sColor.slice(i, i + 2)));
    }
    return sColorChange;
  } else {
    return sColor;
  }
};
export const colorHex = (rgb: any) => {
  const _this = rgb;
  const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
  if (/^(rgb|RGB)/.test(_this)) {
    const aColor = _this.replace(/(?:(|)|rgb|RGB)*/g, '').split(',');
    let strHex = '#';
    for (let i = 0; i < aColor.length; i++) {
      let hex: any = Number(aColor[i]).toString(16);
      hex = hex < 10 ? 0 + '' + hex : hex; // 保证每个rgb的值为2位
      if (hex === '0') {
        hex += hex;
      }
      strHex += hex;
    }
    if (strHex.length !== 7) {
      strHex = _this;
    }
    return strHex;
  } else if (reg.test(_this)) {
    const aNum = _this.replace(/#/, '').split('');
    if (aNum.length === 6) {
      return _this;
    } else if (aNum.length === 3) {
      let numHex = '#';
      for (let i = 0; i < aNum.length; i += 1) {
        numHex += aNum[i] + aNum[i];
      }
      return numHex;
    }
  } else {
    return _this;
  }
};


export const conKeyName: any = {
  ALL_PLT: '全平台',
  OTHER_PLT: '其他平台',
  PLT_DIFF: '平台差异',
  SUCC_BRD: '成功品牌',
  HOT_IW: '爆品及热词',
};

export const getChineseStringLength = (str: string): number => {
  const chineseChars = str.match(/[\u4e00-\u9fa5]/g) || [];
  const otherCharsLength = str.length - chineseChars.length;
  return chineseChars.length * 2 + otherCharsLength;
};
